/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import type Model from '@ember-data/model';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type IntlService from 'ember-intl/services/intl';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type { ReviewableContent } from '../content-review';
import { EntityType } from 'embercom/models/data/entity-types';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';

interface Args {
  showSidePanel: boolean;
  toggleSidePanel: () => void;
  activeContent: LocalizedKnowledgeContent & Model;
  approveVersion: () => void;
  rejectVersion: () => void;
  disableButtons: boolean;
  saveAsDraft: () => void;
  onClose?: () => void;
}

export default class Header extends Component<Args> {
  closeSidePanelOnResize = true;

  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare intl: IntlService;

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get approveButtonLabel() {
    return this.isArticleContent
      ? this.intl.t(
          'knowledge-hub.content-editor.drawer-editor.review-mode-header.approve-and-publish',
        )
      : this.intl.t('knowledge-hub.content-editor.drawer-editor.review-mode-header.approve');
  }

  get title() {
    let content = this.args.activeContent;
    let objectName = objectNames[content.entityType];
    let reviewType = (content as ReviewableContent).contentReviewRequest.get('reviewType');

    if (reviewType === ReviewType.CREATE_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.add-${objectName}`);
    } else if (reviewType === ReviewType.EDIT_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.edit-${objectName}`);
    } else if (reviewType === ReviewType.DELETE_CONTENT) {
      return this.intl.t(`knowledge-hub.content-editor.drawer-editor.titles.delete-${objectName}`);
    }
    return this.intl.t(
      `knowledge-hub.content-editor.drawer-editor.titles.fallback-title-${objectName}`,
    );
  }

  get contentReviewRequest(): ContentReviewRequest {
    return (this.args.activeContent as ReviewableContent).contentReviewRequest;
  }

  get showRejectButton() {
    return this.contentReviewRequest.get('status') === Status.Pending;
  }

  @action closeSidePanel(header: HTMLElement) {
    let headerHasScroll = header.offsetWidth < header.scrollWidth;
    let closeSidePanel = headerHasScroll && this.closeSidePanelOnResize && this.args.showSidePanel;
    this.closeSidePanelOnResize = !headerHasScroll && this.args.showSidePanel;
    if (closeSidePanel) {
      // schedule the side panel to close after it finishes opening, to avoid a race condition
      schedule('afterRender', () => {
        this.args.toggleSidePanel();
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::Header': typeof Header;
    'knowledge-hub/content-editor/content-review/header': typeof Header;
  }
}
